import { useRef } from "react";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { addOrRemove, getHoverTipContent } from "@/Utils/Misc";
import {
  Button,
  Checkbox,
  Counter,
  DatePicker,
  Divider,
  EditableText,
  Flex,
  isNullish,
  LabelWrapper,
  List,
  Menu,
  Select,
  Text
} from "@theorgchartdev/storybook";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { GetWSMgr } from "@/Utils/StateLib";
import { ImportFilterFieldInput } from "./ImportFilterFieldInput";
import { useAsyncEffect } from "@/hooks/useAsyncEffect";
import { appstr } from "@coreapp/utils/appstr";
import { QQueryUniqueValues } from "@coreapp/CoreApp/QQueryUniqueValues";
import { Globals } from "@coreapp/CoreApp/Globals";
import { isValidDate } from "@coreapp/utils/isValidDate";
import { appstrp } from "@coreapp/utils/appstrp";
import { enumFieldType, enumObjectTypes } from "@coreapp/constants";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { getFieldType } from "@coreapp/utils/getFieldType";

const BuildFieldValueOptions = (values, importFilter, onClick) => {
  const existingValues = importFilter.values || [];

  const options = (values ?? [])
    .filter(v => {
      return existingValues.indexOf(v) < 0;
    })
    .map(v => ({
      clickHandler: onClick,
      label: v,
      value: v
    }));

  return [
    ...options,
    {
      clickHandler: onClick,
      label: "(Blank)",
      value: ""
    }
  ];
};

const BuildFieldValues = (importFilter, handlers, dataTestIdPrefix) => {
  const options = [];
  const values = importFilter.values || [];
  const containsVals = importFilter.valContains || [];

  for (const idx in values) {
    const aValueIdx = parseInt(idx);
    const aValue = values[aValueIdx];

    const label = aValue === "" ? "(Blank)" : aValue;
    const includesVal = containsVals.indexOf(aValueIdx) > -1;

    const textContent = (
      <EditableText
        className="oc-fill-width"
        size={"small"}
        bold={false}
        text={label}
        onChange={handlers.onEdit(aValueIdx)}
        customClass={"ocn-folder-row-text"}
      />
    );

    options.push({
      content: (
        <ActionListItem
          contentBefore={textContent}
          actions={[
            {
              tooltipProps: getHoverTipContent("ContainsValueImportFilter"),
              iconName: includesVal ? "circle-check" : "circle-xmark",
              onClick: () => handlers.onToggleContains(aValueIdx),
              dataTestId: `${dataTestIdPrefix}-toggle-contains-${aValueIdx}`
            },
            {
              tooltipProps: getHoverTipContent("DelFieldValuePickerFilter"),
              iconName: "trash-can",
              iconProps: { color: "error" },
              onClick: () => handlers.onDelete(aValueIdx),
              dataTestId: `${dataTestIdPrefix}-delete-${aValueIdx}`
            }
          ]}
        />
      ),
      value: aValue
    });
  }

  return options;
};

export const ImportFilterExclusiveInput = props => {
  const hasDateBracket = !isNullish(props.importFilter.dateBracket);

  const fieldName = props.importFilter.field;
  const fieldFormatType = props.chartConfig?.fieldFormats?.[fieldName]?.type;
  const isDateField = fieldName && getFieldType(fieldName, fieldFormatType) === enumFieldType.date;

  const handleToggleMgrs = () => {
    props.setImportFilter(p => ({ ...p, includefilteredmgrs: !p.includefilteredmgrs }));
  };

  const handleToggleDate = () => {
    props.setImportFilter(prev => {
      const next = { ...prev };
      if (!next["dateBracket"]) {
        // Cleanup
        next["values"] = [];
        delete next["seed"];
        delete next["limitfields"];

        next["dateBracket"] = {};
        next["dateBracket"]["rangeOption"] = enumImportFilterDateRange["Static Range"];
        next["dateBracket"]["range"] = {};
        next["dateBracket"]["range"]["from"] = new Date();
        next["dateBracket"]["range"]["to"] = new Date();
      } else {
        delete next["dateBracket"];
      }

      return next;
    });
  };

  // Should be only shown for the hierarchy type.
  const showManagerCheckbox = !(
    props.importFilter.fieldType === enumObjectTypes.holder && IsPositionChart(props.chartConfig)
  );

  const handleFieldChange = newFieldName => {
    const newFieldFormatType = props.chartConfig?.fieldFormats?.[newFieldName]?.type;
    const isNewFieldDate =
      newFieldName && getFieldType(newFieldName, newFieldFormatType) === enumFieldType.date;

    // Clear dateBracket if switching to a non-date field
    if (!isNewFieldDate && hasDateBracket) {
      handleToggleDate();
    }
  };

  return (
    <>
      <ImportFilterFieldInput {...props} onFieldChange={handleFieldChange} />
      {showManagerCheckbox && (
        <Checkbox
          id="importFilters-filteredMgrs"
          label={appstr("includefiltered")}
          size="medium"
          checked={props.importFilter.includefilteredmgrs}
          onToggle={handleToggleMgrs}
          data-testid={`${props.dataTestIdPrefix}-include-filtered-mgrs-checkbox`}
        />
      )}
      {isDateField && (
        <Checkbox
          id="importFilters-dateBracket"
          label={appstr("dateBracket")}
          size="medium"
          checked={hasDateBracket}
          onToggle={handleToggleDate}
          data-testid={`${props.dataTestIdPrefix}-date-bracket-checkbox`}
        />
      )}
      <Divider />
      {hasDateBracket ? (
        <ImportFilterExclusiveDateValues {...props} />
      ) : (
        <ImportFilterExclusiveFieldValues {...props} />
      )}
    </>
  );
};

const ImportFilterExclusiveFieldValues = props => {
  const fieldValueMenuRef = useRef();

  const { data, loading } = useAsyncEffect(() => {
    if (isNullish(props.importFilter?.field)) {
      return [];
    }

    const wsMgr = GetWSMgr();
    if (!wsMgr) {
      return [];
    }

    return new Promise((resolve, reject) => {
      const callbackObj = {
        QueryUniqueValuesResponse: (success, _fieldName, _colWidth, results) => {
          if (success) {
            resolve(results);
            return;
          }

          reject();
        }
      };

      const dataQuery = new QQueryUniqueValues();
      dataQuery.InitQueryUniques(callbackObj);
      const accountName = wsMgr.GetAccountName();
      const user = wsMgr.GetUserName();

      dataQuery.SetParams(
        accountName,
        user,
        props.importFilter.field,
        props.chartConfig["ID"],
        undefined,
        undefined,
        false,
        props.importFilter.fieldType
      );
      Globals._rfcMgr.QueueRFC(dataQuery);
    });
  }, [props.importFilter?.field]);

  const handleAddFieldValue = option => {
    fieldValueMenuRef.current?.setOpen(false);

    props.setImportFilter(p => ({
      ...p,
      values: [...(p.values || []), option.value]
    }));
  };

  const handleToggleFieldValue = fieldIdx => {
    props.setImportFilter(p => ({
      ...p,
      valContains: addOrRemove(p.valContains || [], fieldIdx)
    }));
  };

  // See "saveValueEdit" command
  // In 531, this does NOT update valContains
  const handleEditFieldValue = fieldIdx => newValue => {
    props.setImportFilter(p => {
      const nextValues = [...p.values];
      nextValues[fieldIdx] = newValue;

      return {
        ...p,
        values: nextValues
      };
    });
  };

  const handleDeleteFieldValue = fieldIdx => {
    props.setImportFilter(p => {
      const nextValues = [...p.values.slice(0, fieldIdx), ...p.values.slice(fieldIdx + 1)];
      let nextValContains = undefined;
      if (Array.isArray(p.valContains) && p.valContains.includes(fieldIdx)) {
        nextValContains = p.valContains.filter(v => v !== fieldIdx);
      }

      return {
        ...p,
        values: nextValues,
        valContains: nextValContains
      };
    });
  };

  const options = BuildFieldValues(
    props.importFilter,
    {
      onToggleContains: handleToggleFieldValue,
      onEdit: handleEditFieldValue,
      onDelete: handleDeleteFieldValue
    },
    props.dataTestIdPrefix
  );

  return (
    <>
      <Flex direction="row" justifyContent="space-between">
        <Text size="medium">{appstr("Filter values")}</Text>
        <Menu
          openCtrlRef={fieldValueMenuRef}
          listWidth={"large"}
          listProps={FieldSelectListProps()}
          popoverProps={{
            flipOptions: {
              mainAxis: false
            }
          }}
          dataTestId={`${props.dataTestIdPrefix}-add-field-value-menu`}
          trigger={
            <Button
              disabled={isNullish(props.importFilter?.field) || loading}
              iconProps={{
                name: "plus"
              }}
              modifier={"secondary"}
              data-testid={`${props.dataTestIdPrefix}-add-field-value-button`}
            >
              {appstr("Add field value")}
            </Button>
          }
          options={BuildFieldValueOptions(data, props.importFilter, handleAddFieldValue)}
        />
      </Flex>
      {options.length > 0 ? (
        <List listWidth="fill" optionSize={"small"} options={options} />
      ) : (
        <Text colorModifier={"lightgrey"}>{appstr("No values have been added")}</Text>
      )}
    </>
  );
};

const enumImportFilterDateRange = {
  "Static Range": "Static Range",
  "Dynamic Range": "Dynamic Range"
};

const BuildRangeOptions = onClick => {
  return Object.values(enumImportFilterDateRange).map(dateRange => ({
    clickHandler: onClick,
    label: appstr(dateRange),
    value: dateRange
  }));
};

const BuildPeriodOptions = onClick => {
  return ["refreshdate", "Days", "Weeks", "Months"].map(period => ({
    clickHandler: onClick,
    label: appstr(period),
    value: period
  }));
};

const BuildBoundOptions = onClick => {
  return ["Bounded", "Left Bound", "Right Bound"].map(bound => ({
    clickHandler: onClick,
    label: appstr(bound),
    value: bound
  }));
};

const ImportFilterExclusiveDateValues = props => {
  const boundedValue = props.importFilter["dateBracket"]["bounds"] ?? "Bounded";
  const isStaticRange =
    props.importFilter["dateBracket"]["rangeOption"] === enumImportFilterDateRange["Static Range"];

  const datePickerProps = (ariaLabel, testIdSuffix) => {
    return {
      placeholderText: appstr("Choose date"),
      portalId: "modal-root",
      popperClassName: "oc-date-picker",
      buttonProps: {
        iconProps: {
          name: "calendar"
        },
        iconAlignEnd: true,
        ariaLabel: ariaLabel,
        dataTestId: `${props.dataTestIdPrefix}-${testIdSuffix}`
      }
    };
  };

  let dateToProps = datePickerProps("Date to", "date-to");
  let dateFromProps = datePickerProps("Date from", "date-from");

  const handleRangeChange = ({ value }) => {
    const nextFilter = structuredClone(props.importFilter);

    const isNew = nextFilter["dateBracket"]["rangeOption"] !== value;
    nextFilter["dateBracket"]["rangeOption"] = value;
    if (isNew) {
      nextFilter["dateBracket"]["range"] = {};
      if (value === enumImportFilterDateRange["Static Range"]) {
        nextFilter["dateBracket"]["range"]["from"] = new Date();
        nextFilter["dateBracket"]["range"]["to"] = new Date();
        delete nextFilter["dateBracket"]["bounds"];
      } else {
        nextFilter["dateBracket"]["range"]["theVal"] = "0";
        nextFilter["dateBracket"]["range"]["period"] = "Weeks";
        delete nextFilter["dateBracket"]["bounds"];
      }
    }

    props.setImportFilter(nextFilter);
  };

  const handleDateRangeChange = key => newDate => {
    if (!isValidDate(newDate)) {
      return;
    }

    const nextFilter = structuredClone(props.importFilter);
    nextFilter["dateBracket"]["range"][key] = newDate;

    props.setImportFilter(nextFilter);
  };

  const handlePeriodChange = ({ value }) => {
    const nextFilter = structuredClone(props.importFilter);
    nextFilter["dateBracket"]["range"]["period"] = value;
    props.setImportFilter(nextFilter);
  };

  const handlePeriodValChange = value => {
    const nextFilter = structuredClone(props.importFilter);
    nextFilter["dateBracket"]["range"]["theVal"] = value;
    props.setImportFilter(nextFilter);
  };

  const handleBoundChange = ({ value }) => {
    const nextFilter = structuredClone(props.importFilter);
    nextFilter["dateBracket"]["bounds"] = value;
    props.setImportFilter(nextFilter);
  };

  return (
    <>
      <Select
        value={props.importFilter["dateBracket"]["rangeOption"]}
        options={BuildRangeOptions(handleRangeChange)}
        dataTestId={`${props.dataTestIdPrefix}-date-range-select`}
      />
      {isStaticRange ? (
        <Flex direction="row" gap="8px">
          <LabelWrapper label={appstr("Date from")}>
            <DatePicker
              selected={props.importFilter["dateBracket"]["range"]["from"]}
              onChange={handleDateRangeChange("from")}
              {...dateFromProps}
            />
          </LabelWrapper>
          <LabelWrapper label={appstr("Date to")}>
            <DatePicker
              selected={props.importFilter["dateBracket"]["range"]["to"]}
              onChange={handleDateRangeChange("to")}
              {...dateToProps}
            />
          </LabelWrapper>
        </Flex>
      ) : (
        <>
          <LabelWrapper label={appstrp("refreshdate", ":")}>
            <Flex gap="8px">
              {props.importFilter["dateBracket"]["range"]["period"] != "refreshdate" ? (
                <Counter
                  onChange={handlePeriodValChange}
                  minValue={-100}
                  currentValue={props.importFilter["dateBracket"]["range"]["theVal"] ?? 0}
                  size={"small"}
                  dataTestIdPrefix={`${props.dataTestIdPrefix}-date-bracket-counter`}
                />
              ) : null}
              <Select
                value={props.importFilter["dateBracket"]["range"]["period"]}
                options={BuildPeriodOptions(handlePeriodChange)}
                dataTestId={`${props.dataTestIdPrefix}-date-bracket-period-select`}
              />
            </Flex>
          </LabelWrapper>
          <Select
            inputOnly={false}
            label={appstr("Bounds")}
            value={boundedValue}
            options={BuildBoundOptions(handleBoundChange)}
            dataTestId={`${props.dataTestIdPrefix}-date-bracket-bounds-select`}
          />
        </>
      )}
    </>
  );
};
